import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { useRef } from "react";
import useSWR, { mutate } from "swr";

const LeadProfile = ({ }) => {
    const [fields, setFields] = useState([]);
    const [lead, setLead] = useState(null);
    const [isFieldsLoading, setIsFieldsLoading] = useState(true);
    const { id } = useParams();
    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});
    const [options, setOptions] = useState({});



    const navigate = useNavigate();

    const idToFetch = id;

    const fetcher = (url) =>
        fetch(url).then((res) => {
            if (!res.ok) throw new Error("Failed to fetch");
            return res.json();
        });

    const { data, error, isLoading } = useSWR(
        "/server/dealer_backend/getLayout?module=Donor_Profiling",
        fetcher,
        {
            revalidateOnFocus: false,   // ⛔ stop refetching on window focus
            revalidateOnReconnect: false, // ⛔ stop refetching on network reconnect
        }
    );


    // ✅ Get all sections with "used" fields
    const sections = (() => {
        if (!data || data.status !== "success") return [];

        const ifrLayout = data.data.find((layout) => layout.layout_name === "Standard");
        if (!ifrLayout) return [];

        return ifrLayout.sections
            .filter(
                (section) =>
                    Array.isArray(section.fields) &&
                    section.fields.some((f) => f.type === "used")
            )
            .map((section) => ({
                ...section,
                fields: section.fields.filter((f) => f.type === "used"),
            }));
    })();

    // ✅ Specifically get Donor Profiling fields (api_names)
    const donorProfilingFields = useMemo(() => {
        const donorProfilingSection = sections.find(
            (section) => section.display_label === "Donor Profiling Information"
        );
        if (!donorProfilingSection) return [];
        return donorProfilingSection.fields.map((f) => f.name);
    }, [sections]); // recompute only if sections change




    const fetchPickListValues = async (id) => {
        // console.log(id)
        const module = "Donor_Profiling";
        try {
            const response = await fetch(`/server/dealer_backend/getPickListValues/${id}?module=${module}`);
            const data = await response.json();

            if (data.status === "success") {
                setOptions((prev) => ({
                    ...prev,
                    [id]: data.data,
                }));
            } else {
                // console.error("Failed to fetch pick list values:", data.error);
            }
        } catch (error) {
            // console.error("Error fetching pick list values:", error);
        }
    };


    const handleInputChange = useCallback((e) => {
        const { name, type, value, checked } = e.target;
        const newValue = type === "checkbox" ? checked : value;

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        // Validate only this field
        const fieldErrors = validateForm({ ...formData, [name]: newValue });
        setErrors((prev) => ({
            ...prev,
            [name]: fieldErrors[name] || "",
        }));
    }, [formData]);



    useEffect(() => {
        const listValues = async () => {
            const module = "Donor_Profiling";
            try {
                const response = await fetch(`/server/dealer_backend/getDataTypes?module=${module}`);
                const data = await response.json();
                // console.log(data);

                if (data.status === "success") {
                    setFields(data.data);
                } else {
                    // console.error("Failed to fetch  values:", data.error);
                }
            } catch (error) {
                // console.error("Error fetching  values:", error);
            }
        };

        listValues();
    }, []);


    const fetchLeadData = async () => {
        if (donorProfilingFields.length === 0) return;

        setIsFieldsLoading(true);
        try {
            const response = await fetch(
                `/server/dealer_backend/getSpecificContactDonorProfiling?id=${idToFetch}&fields=${donorProfilingFields.join(",")}`
            );
            const data = await response.json();

            if (data.status === "success" && data.data[0]) {
                setLead(data.data[0]);
                setFormData(data.data[0]);
            }
        } catch (error) {
            console.error("Failed to fetch lead data:", error);
        } finally {
            setIsFieldsLoading(false);
        }
    };


    useEffect(() => {
        fetchLeadData();
    }, [idToFetch, JSON.stringify(donorProfilingFields)]);




    const handleFieldChange = async (label, value) => {
        // console.log(label)
        const apiName = labelToApiNameMap.current[label];
        const updatedFormData = { ...formData, [apiName]: value };


        if (label === "Zip Code" && value.length === 6) {
            try {
                const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);

                const data = await response.json();
                // console.log("API response:", data[0].PostOffice[0]);

                if (data[0].PostOffice[0] && data[0].PostOffice[0].State && data[0].PostOffice[0].District) {
                    updatedFormData[labelToApiNameMap.current["City"]] = data[0].PostOffice[0].District;
                    updatedFormData[labelToApiNameMap.current["State"]] = data[0].PostOffice[0].State;
                    updatedFormData[labelToApiNameMap.current["Country"]] = data[0].PostOffice[0].Country || "India";
                }
            } catch (err) {
                // console.error("Deluge API call failed:", err);
            }
        }



        setFormData(updatedFormData);
    };


    const labelToApiNameMap = useRef({});





    const renderInputField = (field) => {
        const fieldValue = formData[field.name] || "";
        const isReadOnlyLeadStatus = field.field_label === "";

        // Special rendering for "Catalyst User"


        // Main switch
        switch (field.data_type) {
            case "integer":
            case "currency":
            case "bigint":
                if (field.display_label === "Donation Amount") {
                    return (
                        <input
                            type="number"
                            name={field.name}
                            min="0"
                            step="0.01"
                            value={formData[field.name] || ""}
                            onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                if (value >= 0 || e.target.value === "") {
                                    handleInputChange(e);
                                }
                            }}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${errors[field.name] ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                    );
                }

                return <input type="number" name={field.name} value={fieldValue} onChange={handleInputChange} step="0.01" />;


            case "datetime":
                return <input type="datetime-local" name={field.name} value={fieldValue} onChange={handleInputChange} />;

            case "date":
                return <input type="date" name={field.name} value={fieldValue} onChange={handleInputChange} />;

            case "text":
            case "email":
            case "ownerlookup":
            case "phone":
            case "website":
                // 👇 Special handling for First Name
                // if (field.display_label === "First Name") {
                //   return (
                //     <div className="flex w-[300px] rounded border overflow-hidden">
                //       {/* Salutation Dropdown */}
                //       <select
                //         className="px-2 py-2 bg-white border-r border-gray-300 outline-none focus:ring-0"
                //         value={formData.Salutation || ""}
                //         onChange={(e) => {
                //           const Salutation = e.target.value;
                //           setFormData(prev => ({
                //             ...prev,
                //             Salutation,
                //           }));
                //         }}
                //       >
                //         <option value="">-None-</option>
                //         <option value="Mr.">Mr.</option>
                //         <option value="Mrs.">Mrs.</option>
                //         <option value="Ms.">Ms.</option>
                //         <option value="Dr.">Dr.</option>
                //         <option value="Prof.">Prof.</option>
                //       </select>

                //       {/* First Name Input */}
                //       <input
                //         type="text"
                //         placeholder="Enter First Name"
                //         className="flex-1 px-2 py-2 outline-none focus:ring-0"
                //         value={formData.First_Name || ""}
                //         onChange={(e) => {
                //           const firstName = e.target.value;
                //           setFormData(prev => ({
                //             ...prev,
                //             First_Name: firstName, // ✅ Store clean first name
                //           }));
                //         }}
                //       />
                //     </div>
                //   );
                // }



                // 👇 Normal text/email/phone fields
                return (
                    <div className="w-full">
                        <input
                            type={field.type}
                            name={field.name}
                            value={formData[field.name] || ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (field.display_label === "Zip Code") {
                                    handleFieldChange(field.display_label, val);
                                } else {
                                    handleInputChange(e);
                                }
                            }}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${errors[field.name] ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors[field.name] && (
                            <p className="text-sm text-red-500 mt-1">{errors[field.name]}</p>
                        )}
                    </div>
                );

            case "boolean":
                return <input type="checkbox" name={field.name} checked={formData[field.name] || false} onChange={handleInputChange} />;

            case "picklist":
                const picklistValues = options[field.id];
                return (
                    <select
                        name={field.name}
                        value={fieldValue}
                        onChange={isReadOnlyLeadStatus ? undefined : handleInputChange}
                        disabled={isReadOnlyLeadStatus}
                    >
                        {!picklistValues ? (
                            <option disabled>Loading...</option>
                        ) : (
                            picklistValues.map((option, idx) => (
                                <option key={idx} value={option}>
                                    {option}
                                </option>
                            ))
                        )}
                    </select>
                );


            case "profileimage":
                return <input type="file" name={field.name} onChange={handleInputChange} accept="image/*" />;

            case "lookup":
                return (
                    <select name={field.name} value={fieldValue} onChange={handleInputChange}>
                        {field.lookupValues?.length ? (
                            field.lookupValues.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.label}
                                </option>
                            ))
                        ) : (
                            <option disabled>No options available</option>
                        )}
                    </select>
                );

            default:
                return <input type="text" name={field.name} value={fieldValue} onChange={handleInputChange} />;
        }
    };

    const [saving, setSaving] = useState(false);

    // console.log({formData})

    // Validation function
    const validateForm = (formData) => {
        let errors = {};

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/;

        // Full Name (mandatory)
        if (!formData.Name?.trim()) {
            errors.Name = "Full Name cannot be empty.";
        }

        // Phone (only if user typed something)
        if (formData.Phone?.trim() && !phoneRegex.test(formData.Phone)) {
            errors.Phone = "Phone number must be a 10-digit number.";
        }

        // Primary Email (only if user typed something)
        if (formData.Email?.trim() && !emailRegex.test(formData.Email)) {
            errors.Email = "Please enter a valid email address.";
        }

        // Secondary Email (only if user typed something)
        if (formData.Secondary_Email?.trim() && !emailRegex.test(formData.Secondary_Email)) {
            errors.Secondary_Email = "Please enter a valid email address.";
        }

        return errors;
    };


    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        const newErrors = validateForm(formData);

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);

            // Show first error message in toast
            const firstError = Object.values(newErrors)[0];
            toast.error(firstError);

            setSaving(false);
            return;
        }

        // ✅ No validation errors → proceed with API call
        const { Modified_By, Layout, ...updatedFormData } = formData;
        const requestBody = {
            data: [
                {
                    ...updatedFormData,
                    Owner: updatedFormData.Owner ? parseInt(updatedFormData.Owner, 10) : undefined,
                    Created_By: updatedFormData.Created_By ? parseInt(updatedFormData.Created_By, 10) : undefined,
                    Existing_Donor: idToFetch,
                    Catalyst_User_Email : lead?.Catalyst_User_Email || "",



                },
            ],
        };

        try {
            const response = await fetch(`/server/dealer_backend/edit-donorDetails/${idToFetch}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json();

            if (result.status === "success") {
                toast.success("Details added successfully!");
                navigate(`/contact-details/${idToFetch}`);
            } else {
                toast.error(result.error || "Failed to update data");
                setSaving(false);
            }
        } catch (error) {
            toast.error("An error occurred while updating the data");
            setSaving(false);
        }
    };







    const fetched = useRef(false);

    useEffect(() => {
        if (fetched.current) return; // already ran
        if (!sections || sections.length === 0) return; // wait for data

        // Fetch picklist values
        sections.forEach((section) => {
            section.fields?.forEach((field) => {
                if (field.data_type === "picklist" && field.id) {
                    fetchPickListValues(field.id);
                }
            });
        });

        // Map display labels
        const map = {};
        sections.forEach(section => {
            section.fields.forEach(field => {
                map[field.display_label] = field.name;
            });
        });
        labelToApiNameMap.current = map;

        fetched.current = true; // mark as done
    }, [sections]);










    return (
        <div className="LeadsPage">

            <ToastContainer position="top-right" autoClose={3000} />

            {(isLoading || !formData) ? (
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
                </div>
            ) : (

                <div className="edit-container">

                    <form>
                        {/* Section 1: Basic Information - Two Columns */}
                        <div className="form-section two-columns">
                            <div className="lead-buttons">
                                <h3 className="text-xl font-bold">Edit Existing Donor</h3>
                                <div>

                                    <button
                                        className="save-button mb-2"
                                        onClick={handleSave}
                                        disabled={saving} // disable while saving
                                    >
                                        {saving ? "Saving..." : "Save"}
                                    </button>


                                    <button
                                        className="cancel-button mb-2"
                                        onClick={(e) => {
                                            e.preventDefault(); // prevent any form-related default behavior
                                            navigate(`/contact-details/${idToFetch}`);
                                        }}

                                    >
                                        Cancel
                                    </button>

                                </div>
                            </div>
                            <hr className="mb-4" />
                            <div className="lead-form">
                                {sections
                                    .filter(section =>
                                        section.type === "used" &&
                                        !["Donor Profiling Image", "Address Information", "Lead Generating Teams and Locations", "Other Details", "Remarks Section", "Nach/ECS Related Info", "Catalyst User Information", "System Information", "Visit Summary"].includes(section.display_label) &&
                                        (section.display_label !== "Face to Face Information" ||
                                            formData[labelToApiNameMap.current["IFR Lead Source"]] === "Face to Face")
                                    )
                                    .map(section => {
                                        let sectionFields = section.fields.filter(
                                            field => field.type === "used" && field.field_label
                                        );

                                        if (section.display_label === "Donor Profiling Information") {
                                            const excludedFields = ["Salutation", "Catalyst User Email","Currency", "Exchange Rate", "First Name", "Modified By", "Modified Time", "Created By", "Created Time", "Tag", "Last Activity Time", "Unsubscribed Time", "Unsubscribed Mode", "Record Id", "Record Status", "Locked", "Existing Donor", "Donor Profiling Owner"];
                                            sectionFields = sectionFields.filter(field => !excludedFields.includes(field.field_label));
                                        }


                                        if (section.display_label === "Description Information") {
                                            const excludedFields = [
                                                "Already a Existing Donor?", "Follow Up Date Time", "Number of RNR", "Last Call Date"
                                            ];
                                            sectionFields = sectionFields.filter(field => !excludedFields.includes(field.field_label));
                                        }

                                        if (sectionFields.length === 0) return null;

                                        return (
                                            <div key={section.display_label} className="form-section">
                                                <h3>{section.display_label}</h3>
                                                <div className={`grid gap-2 ${section.column_count === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                                                    {sectionFields.map((field) => {
                                                        // If the field is "Last Name", rename it
                                                        const label = field.field_label === "Last Name" ? "Full Name" : field.field_label;

                                                        const api = labelToApiNameMap.current;
                                                        const isStatusInterested = formData[api["Status"]] === "Interested Now";

                                                        const alwaysRequired = ["Full Name"];
                                                        const conditionallyRequired = ["ID Proof Type", "ID Proof Number"];
                                                        const isAlwaysRequired = alwaysRequired.includes(label);
                                                        const isConditionallyRequired = isStatusInterested && conditionallyRequired.includes(label);

                                                        return (
                                                            <div key={field.field_label} className="form-fields">
                                                                <label htmlFor={field.field_label} className="block mb-1">
                                                                    {label}
                                                                    {(isAlwaysRequired || isConditionallyRequired) && (
                                                                        <span className="text-red-500"> *</span>
                                                                    )}
                                                                </label>
                                                                {renderInputField(field)}
                                                            </div>
                                                        );
                                                    })}

                                                </div>
                                            </div>
                                        );
                                    })}

                            </div>


                            {/* <div className="form-section">
                <div className="lead-form">
                  <h3>Description Information</h3>
                  <div className="form-fields-container">
                    {fields
                      .filter(field => ["Calling date", "Calling done by", "Description"].includes(field.label))
                      .map((field, index) => (
                        <div key={index} className="form-fields">
                          <label htmlFor={field.label.toLowerCase()}>{field.label}</label>
                          {renderInputField(field)}
                        </div>
                      ))}
                  </div>
                </div>
              </div> */}

                        </div>




                    </form>



                </div>
            )}
        </div>
    );
};

export default LeadProfile;
